/**
 * Sessions employé (worker_sessions_*) — lecture unifiée + décompression envelope.
 * Les clés contiennent "business_" et étaient compressées par autoSave → parse [] sans unwrap.
 */

import { kvGet, kvSet, kvSetPersisted } from '../infrastructure/storage/kvAccess';
import { getStorageEngine } from '../app/di/container';
import { unwrapCompressionEnvelopeValue } from './localStorageCompression';
import { normalizeBusinessStorageId } from './sharedCore';
import { parseKvObject } from './cashRegisterDataStorage';

export function workerSessionStorageKey(businessId, workerId) {
  const bid = normalizeBusinessStorageId(businessId);
  return `worker_sessions_${bid}_${workerId}`;
}

/** Toutes les clés legacy possibles pour un employé. */
export function legacyWorkerSessionKeysForWorker(businessId, workerId) {
  const raw = String(businessId ?? '');
  const normalized = normalizeBusinessStorageId(businessId);
  const keys = [];
  const add = (k) => {
    if (k && !keys.includes(k)) keys.push(k);
  };

  add(workerSessionStorageKey(businessId, workerId));
  add(`worker_sessions_${raw}_${workerId}`);
  add(`worker_sessions_${normalized}_${workerId}`);
  if (raw && !raw.startsWith('business_')) {
    add(`worker_sessions_business_${raw}_${workerId}`);
  }
  if (normalized !== raw) {
    add(`worker_sessions_business_${normalized}_${workerId}`);
  }
  return keys;
}

/** Lit et fusionne les sessions d'un employé (toutes variantes de clé). */
export function loadSessionsForWorker(businessId, workerId) {
  const collected = [];

  legacyWorkerSessionKeysForWorker(businessId, workerId).forEach((key) => {
    loadSessionsFromKey(key).forEach((session) => {
      if (session && typeof session === 'object') collected.push(session);
    });
  });

  return mergeWorkerSessionList(collected).sort(
    (a, b) => new Date(a.loginTime || 0) - new Date(b.loginTime || 0)
  );
}

/** Schreibt Sessions als Klartext-Array (kein Kompressions-Envelope). */
export function persistWorkerSessions(businessId, workerId, sessions) {
  const key = workerSessionStorageKey(businessId, workerId);
  kvSet(key, Array.isArray(sessions) ? sessions : []);
  void syncWorkerSessionsToEntity(businessId);
  return key;
}

/** Persistiert Sessions sofort (Logout / Stromausfall-sicher). */
export async function persistWorkerSessionsPersisted(businessId, workerId, sessions) {
  const key = workerSessionStorageKey(businessId, workerId);
  await kvSetPersisted(key, Array.isArray(sessions) ? sessions : []);
  await syncWorkerSessionsToEntity(businessId);
  return key;
}

/**
 * Spiegelt alle worker_sessions_* in entity_workerSessions (NO_KV_BLOB).
 */
export async function syncWorkerSessionsToEntity(businessId) {
  const bid = normalizeBusinessStorageId(businessId);
  if (!bid) return;

  const allSessions = loadAllWorkerSessionsForBusiness(bid);
  try {
    const { queueBusinessPayloadFieldWrite } = await import(
      '../infrastructure/storage/businessPayloadWrite'
    );
    await queueBusinessPayloadFieldWrite(bid, 'workerSessions', allSessions);
    const { refreshWorkerSessionsReadCache } = await import('./workerSessionsReadSource');
    await refreshWorkerSessionsReadCache(bid);
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[workerSessionsStorage] entity sync failed:', err?.message || err);
    }
  }
}

/** Préfixes pour lister worker_sessions_<business>_<workerId>. */
export function legacyWorkerSessionKeyPrefixes(businessId) {
  const raw = String(businessId);
  const normalized = normalizeBusinessStorageId(businessId);
  const prefixes = [];

  const add = (p) => {
    if (p && !prefixes.includes(p)) prefixes.push(p);
  };

  add(`worker_sessions_${raw}_`);
  add(`worker_sessions_${normalized}_`);
  if (!raw.startsWith('business_')) add(`worker_sessions_business_${raw}_`);
  if (normalized !== raw) add(`worker_sessions_business_${normalized}_`);

  return prefixes;
}

/** Clé agrégée legacy (sans workerId). */
export function legacyAggregateWorkerSessionKeys(businessId) {
  const raw = String(businessId);
  const normalized = normalizeBusinessStorageId(businessId);
  const keys = [];

  const add = (k) => {
    if (k && !keys.includes(k)) keys.push(k);
  };

  add(`worker_sessions_${raw}`);
  add(`worker_sessions_${normalized}`);
  if (!raw.startsWith('business_')) add(`worker_sessions_business_${raw}`);
  if (normalized !== raw) add(`worker_sessions_business_${normalized}`);

  return keys;
}

function newCashRegisterDataKeys(businessId) {
  const raw = String(businessId);
  const normalized = normalizeBusinessStorageId(businessId);
  const keys = [];
  const add = (k) => {
    if (k && !keys.includes(k)) keys.push(k);
  };
  add(`newCashRegisterData_${raw}`);
  add(`newCashRegisterData_${normalized}`);
  return keys;
}

function mapNewCashRegisterSessionToWorkerSession(session) {
  if (!session || typeof session !== 'object') return null;
  return {
    id: session.sessionId || session.id,
    workerId: session.workerId,
    workerName: session.workerName,
    loginTime: session.loginTime,
    logoutTime: session.logoutTime,
    initialAmount: session.initialAmount,
    finalCashAmount: session.finalAmount ?? session.finalCashAmount,
    cashAmount: session.finalAmount ?? session.cashAmount ?? session.initialAmount,
    status: session.status,
    _source: 'newCashRegisterData'
  };
}

/** Score pour garder l'enregistrement le plus complet lors d'une fusion. */
export function sessionRecordRichness(session) {
  if (!session || typeof session !== 'object') return 0;
  let score = 0;
  if (session._source === 'newCashRegisterData') score -= 8;
  if (Array.isArray(session.sales) && session.sales.length > 0) score += 20;
  if (session.predecessorInfo) score += 4;
  if (session.saleCount > 0) score += 3;
  if (session.totalSales > 0) score += 3;
  if (session.initialElectronicAmount > 0 || session.electronicAmount > 0) score += 2;
  if (session.finalCashAmount != null || session.finalAmount != null) score += 2;
  if (session.workerName) score += 1;
  if (session.id && String(session.id).startsWith('session_')) score += 2;
  return score;
}

function toEpochSeconds(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

/** Clé stable pour dédupliquer (tolère légères différences d'horodatage). */
export function sessionDedupeIdentity(session) {
  if (!session || typeof session !== 'object') return '';
  const workerId = String(session.workerId || '');
  const loginSec = toEpochSeconds(session.loginTime);
  const logoutSec = toEpochSeconds(session.logoutTime);
  return `${workerId}|${loginSec ?? 'na'}|${logoutSec ?? 'open'}`;
}

export function sessionsLikelySame(a, b) {
  if (!a || !b) return false;
  if (String(a.workerId || '') !== String(b.workerId || '')) return false;

  const loginA = new Date(a.loginTime || 0).getTime();
  const loginB = new Date(b.loginTime || 0).getTime();
  if (Number.isFinite(loginA) && Number.isFinite(loginB) && Math.abs(loginA - loginB) <= 5000) {
    return true;
  }

  const logoutA = a.logoutTime ? new Date(a.logoutTime).getTime() : null;
  const logoutB = b.logoutTime ? new Date(b.logoutTime).getTime() : null;
  if (
    logoutA != null &&
    logoutB != null &&
    Number.isFinite(logoutA) &&
    Number.isFinite(logoutB) &&
    Math.abs(logoutA - logoutB) <= 5000 &&
    Number.isFinite(loginA) &&
    Number.isFinite(loginB) &&
    Math.abs(loginA - loginB) <= 120000
  ) {
    return true;
  }

  return sessionDedupeIdentity(a) === sessionDedupeIdentity(b);
}

/** Fusionne des doublons (worker_sessions + copies NCR) en gardant la fiche la plus riche. */
export function mergeWorkerSessionList(sessions = []) {
  const merged = [];

  for (const session of sessions) {
    if (!session || typeof session !== 'object') continue;

    const matchIndex = merged.findIndex((existing) => sessionsLikelySame(existing, session));
    if (matchIndex < 0) {
      merged.push({ ...session });
      continue;
    }

    const existing = merged[matchIndex];
    const richer =
      sessionRecordRichness(session) >= sessionRecordRichness(existing) ? session : existing;
    const poorer = richer === session ? existing : session;
    merged[matchIndex] = {
      ...poorer,
      ...richer,
      sales:
        Array.isArray(richer.sales) && richer.sales.length > 0
          ? richer.sales
          : poorer.sales,
      predecessorInfo: richer.predecessorInfo || poorer.predecessorInfo
    };
  }

  return merged;
}

/**
 * @param {string} [key] — pour unwrap compression
 * @param {unknown} value — valeur kvGet
 * @returns {Array}
 */
export function parseWorkerSessionsKvValue(key, value) {
  if (value == null || value === '') return [];

  const toArray = (v) => {
    if (v == null) return [];
    if (Array.isArray(v)) return v.filter((s) => s && typeof s === 'object');
    if (typeof v === 'object' && Array.isArray(v.sessions)) {
      return v.sessions.filter((s) => s && typeof s === 'object');
    }
    return [];
  };

  const unwrapOnce = (v) => {
    if (v == null) return v;
    if (typeof v === 'string') {
      try {
        return JSON.parse(v);
      } catch {
        return v;
      }
    }
    if (typeof v === 'object' && !Array.isArray(v) && key) {
      const inner = unwrapCompressionEnvelopeValue(key, v);
      if (inner !== v) return inner;
    }
    return v;
  };

  let current = unwrapOnce(value);
  if (typeof current === 'object' && !Array.isArray(current) && key) {
    current = unwrapCompressionEnvelopeValue(key, current);
  }
  const arr = toArray(current);
  if (arr.length) return arr;

  if (typeof current === 'string') {
    const again = unwrapOnce(current);
    return toArray(again);
  }

  return [];
}

export function listWorkerSessionKeysForBusiness(businessId) {
  const prefixes = legacyWorkerSessionKeyPrefixes(businessId);
  const allKeys = getStorageEngine().listKvKeys();
  const matched = allKeys.filter(
    (k) => k && prefixes.some((prefix) => k.startsWith(prefix))
  );

  legacyAggregateWorkerSessionKeys(businessId).forEach((legacyKey) => {
    if (allKeys.includes(legacyKey) && !matched.includes(legacyKey)) {
      matched.push(legacyKey);
    }
  });

  return matched;
}

export function loadSessionsFromKey(key) {
  return parseWorkerSessionsKvValue(key, kvGet(key));
}

function loadNewCashRegisterWorkerSessions(businessId) {
  for (const key of newCashRegisterDataKeys(businessId)) {
    const raw = kvGet(key);
    if (raw == null) continue;
    const { corrupted, data } = parseKvObject(raw);
    if (corrupted || !data) continue;
    const sessions = Array.isArray(data.sessions) ? data.sessions : [];
    if (!sessions.length) continue;
    const activeId =
      data.activeSession?.sessionId || data.activeSession?.id || null;
    return sessions
      .map(mapNewCashRegisterSessionToWorkerSession)
      .filter(Boolean)
      .filter((session) => {
        if (session.logoutTime) return true;
        if (activeId && (session.id === activeId || session.sessionId === activeId)) {
          return true;
        }
        return false;
      });
  }
  return [];
}

/** Sessions ouvertes uniquement depuis worker_sessions_* (pas l'historique NCR). */
export function loadOpenWorkerSessionsFromKeys(businessId) {
  const open = [];
  const pushOpen = (session) => {
    if (session && !session.logoutTime) open.push(session);
  };

  listWorkerSessionKeysForBusiness(businessId).forEach((key) => {
    loadSessionsFromKey(key).forEach(pushOpen);
  });

  legacyAggregateWorkerSessionKeys(businessId).forEach((key) => {
    parseWorkerSessionsKvValue(key, kvGet(key)).forEach(pushOpen);
  });

  return open;
}

/**
 * Toutes les sessions d'une entreprise (tous employés), dédupliquées.
 * Source principale : worker_sessions_* (sessionRecorder).
 * newCashRegisterData : uniquement en secours si aucune fiche équivalente.
 */
export function loadAllWorkerSessionsForBusiness(businessId) {
  const collected = [];

  listWorkerSessionKeysForBusiness(businessId).forEach((key) => {
    loadSessionsFromKey(key).forEach((session) => {
      if (session && typeof session === 'object') collected.push(session);
    });
  });

  legacyAggregateWorkerSessionKeys(businessId).forEach((key) => {
    parseWorkerSessionsKvValue(key, kvGet(key)).forEach((session) => {
      if (session && typeof session === 'object') collected.push(session);
    });
  });

  const fromWorkerSessions = mergeWorkerSessionList(collected);

  const ncrOnly = loadNewCashRegisterWorkerSessions(businessId).filter(
    (ncrSession) => !fromWorkerSessions.some((existing) => sessionsLikelySame(existing, ncrSession))
  );

  return mergeWorkerSessionList([...fromWorkerSessions, ...ncrOnly]).sort(
    (a, b) => new Date(b.loginTime || 0) - new Date(a.loginTime || 0)
  );
}
